import {
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	SaveOutlined,
	UndoOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Empty } from "components/Empty/Empty"
import { Form } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { InputNumber } from "components/InputNumber/InputNumber"
import { Select } from "components/Select/Select"
import { Switch } from "components/Switch/Switch"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { Table } from "components/Table/Table"
import { Tooltip } from "components/Tooltip/Tooltip"
import { GlobalConfig } from "interface/globalConfig"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
	useAllGlobalConfigs,
	useGlobalConfigUpdate,
} from "services/globalConfig"
import { humanReadable, sortAlpha } from "utils"

export function AdminGlobalConfig() {
	const { data: allConfigs = [], isLoading } = useAllGlobalConfigs()
	const [selectedGroup, setSelectedGroup] = useState<string | undefined>(
		undefined,
	)
	const [selectedCode, setSelectedCode] = useState<string | undefined>(
		undefined,
	)
	const [selectedFieldKey, setSelectedFieldKey] = useState<string>("")
	const groups = useMemo(() => {
		return Array.from(new Set(allConfigs.map((c) => c.group))).toSorted(
			sortAlpha,
		)
	}, [allConfigs])
	const availableCodes = useMemo(() => {
		if (!selectedGroup) {
			return []
		}
		return allConfigs
			.filter((c) => c.group === selectedGroup)
			.map((c) => c.code)
			.toSorted(sortAlpha)
	}, [allConfigs, selectedGroup])
	// Current active config document
	const currentConfig = useMemo(() => {
		if (!selectedGroup || !selectedCode) {
			return null
		}
		return (
			allConfigs.find(
				(c) => c.group === selectedGroup && c.code === selectedCode,
			) ?? null
		)
	}, [allConfigs, selectedGroup, selectedCode])

	const availableFieldKeys = useMemo(() => {
		if (!currentConfig?.data) {
			return []
		}
		return Object.keys(currentConfig.data as Record<string, any>).toSorted(
			sortAlpha,
		)
	}, [currentConfig])

	// Single unified effect for chained selection management (Group -> Code -> Property)
	useEffect(() => {
		if (selectedGroup && availableCodes.length > 0) {
			if (!selectedCode || !availableCodes.includes(selectedCode)) {
				setSelectedCode(availableCodes[0])
			}
		} else if (!selectedGroup) {
			setSelectedCode(undefined)
		}

		if (availableFieldKeys.length > 0) {
			if (!selectedFieldKey || !availableFieldKeys.includes(selectedFieldKey)) {
				setSelectedFieldKey(availableFieldKeys[0])
			}
		} else {
			setSelectedFieldKey("")
		}
	}, [
		selectedGroup,
		availableCodes,
		selectedCode,
		availableFieldKeys,
		selectedFieldKey,
	])

	return (
		<div className="mx-auto flex flex-col gap-6">
			<h2 className="font-nunito text-3xl font-semibold">
				Global Configurations
			</h2>

			<div className="flex flex-wrap items-center gap-2">
				<Form.Item label="Group" layout="vertical" className="mb-0">
					<Select
						aria-label="Group"
						value={selectedGroup}
						loading={isLoading}
						className="min-w-xs"
						onChange={setSelectedGroup}
						placeholder="Select group..."
						options={groups.map((value) => ({
							value,
							label: humanReadable(value),
						}))}
					/>
				</Form.Item>
				<Form.Item label="Code" layout="vertical" className="mb-0">
					<Select
						aria-label="Code"
						loading={isLoading}
						value={selectedCode}
						className="min-w-xs"
						onChange={setSelectedCode}
						placeholder="Select code..."
						disabled={!selectedGroup || availableCodes.length === 0}
						options={availableCodes.map((code) => ({
							value: code,
							label: humanReadable(code),
						}))}
					/>
				</Form.Item>
				{availableFieldKeys.length > 1 && (
					<Form.Item label="Property" layout="vertical" className="mb-0">
						<Select
							aria-label="Property"
							className="min-w-xs"
							value={selectedFieldKey}
							onChange={setSelectedFieldKey}
							disabled={!currentConfig || availableFieldKeys.length <= 1}
							options={availableFieldKeys.map((key) => ({
								label: key,
								value: key,
							}))}
						/>
					</Form.Item>
				)}
			</div>
			{isLoading ? (
				<div className="py-12 text-center">
					<Spinner size="large" />
				</div>
			) : !currentConfig ? (
				<div className="py-12 text-center">
					<Empty
						description={
							selectedGroup && selectedCode
								? `No configuration entry found for group "${humanReadable(
										selectedGroup,
									)}" and code "${humanReadable(selectedCode)}"`
								: "Please select a group and code to view and edit configurations"
						}
					/>
				</div>
			) : (
				<ConfigEditorTable
					key={currentConfig.id}
					config={currentConfig}
					activeFieldKey={selectedFieldKey}
				/>
			)}
		</div>
	)
}

/**
 * Allows viewing and editing configuration data directly in place without Card wrappers or modals.
 */
function ConfigEditorTable({
	config,
	activeFieldKey,
}: {
	config: GlobalConfig
	activeFieldKey: string
}) {
	const { notify } = useNotification()
	const updateMutation = useGlobalConfigUpdate()
	// Working state for inline edits
	const [workingData, setWorkingData] = useState<Record<string, any>>(
		config.data,
	)
	const [isEditingRow, setIsEditingRow] = useState(false)
	const isDirty = useMemo(() => {
		return JSON.stringify(workingData) !== JSON.stringify(config.data ?? {})
	}, [workingData, config.data])
	const fieldKeys = useMemo(() => {
		const keys = Object.keys(workingData)
		if (activeFieldKey && keys.includes(activeFieldKey)) {
			return [activeFieldKey]
		}
		return keys
	}, [workingData, activeFieldKey])
	const targetArrayOfObjectsFieldKey = useMemo(() => {
		const checkIsObjectArray = (key: string) => {
			const arr = workingData[key]
			if (!Array.isArray(arr)) {
				return false
			}
			if (arr.length > 0) {
				return typeof arr[0] === "object" && arr[0] !== null
			}
			return false
		}

		if (activeFieldKey && checkIsObjectArray(activeFieldKey)) {
			return activeFieldKey
		}
		const objectArrayKey = Object.keys(workingData).find(checkIsObjectArray)
		return objectArrayKey ?? null
	}, [workingData, activeFieldKey])
	const handleReset = () => {
		setWorkingData(config.data)
	}
	const handleSave = () => {
		updateMutation.mutate(
			{
				id: config.id,
				group: config.group,
				code: config.code,
				data: workingData,
			},
			{
				onSuccess: () =>
					notify.success({
						message: `Configuration for ${humanReadable(config.group)}/${humanReadable(config.code)} updated successfully`,
					}),
				onError: (error) =>
					notify.error({
						message: error?.message || "Failed to save configuration",
					}),
			},
		)
	}
	const handleAddRow = () => {
		if (!targetArrayOfObjectsFieldKey) {
			return
		}
		const currentArray =
			(workingData[targetArrayOfObjectsFieldKey] as any[]) || []
		let newObj: any = {}
		if (
			currentArray.length > 0 &&
			typeof currentArray[0] === "object" &&
			currentArray[0] !== null
		) {
			const sample = currentArray[0]
			for (const k of Object.keys(sample)) {
				if (k === "_isNew") {
					continue
				}
				const sampleVal = sample[k]
				if (Array.isArray(sampleVal)) {
					newObj[k] = []
				} else if (typeof sampleVal === "boolean") {
					newObj[k] = false
				} else if (typeof sampleVal === "number") {
					newObj[k] = 0
				} else {
					newObj[k] = ""
				}
			}
		}

		newObj._isNew = true

		setWorkingData({
			...workingData,
			[targetArrayOfObjectsFieldKey]: [newObj, ...currentArray],
		})
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Button
						icon={<UndoOutlined />}
						onClick={handleReset}
						disabled={!isDirty || updateMutation.isPending || isEditingRow}
					>
						Reset
					</Button>
					<Button
						type="primary"
						icon={<SaveOutlined />}
						onClick={handleSave}
						loading={updateMutation.isPending}
						disabled={!isDirty || isEditingRow}
					>
						Save changes
					</Button>
				</div>
				{targetArrayOfObjectsFieldKey && (
					<Button
						type="dashed"
						icon={<PlusOutlined />}
						onClick={handleAddRow}
						disabled={isEditingRow}
					>
						Add row
					</Button>
				)}
			</div>

			<div className="flex flex-col gap-2">
				{fieldKeys.length === 0 ? (
					<Empty description="No fields found in configuration" />
				) : (
					fieldKeys.map((key) => (
						<FieldEditor
							key={key}
							fieldKey={key}
							value={workingData[key]}
							onChange={(newVal) => {
								setWorkingData({ ...workingData, [key]: newVal })
							}}
							onEditingChange={setIsEditingRow}
						/>
					))
				)}
			</div>
		</div>
	)
}

/**
 * Renders inline table or controls for a specific property field.
 */
function FieldEditor({
	fieldKey,
	value,
	onChange,
	onEditingChange,
}: {
	fieldKey: string
	value: any
	onChange: (newValue: any) => void
	onEditingChange?: (isEditing: boolean) => void
}) {
	const isArray = Array.isArray(value)
	const isArrayOfObjects =
		isArray &&
		(value.length > 0
			? typeof value[0] === "object" && value[0] !== null
			: false)

	if (isArrayOfObjects) {
		return (
			<EditableTable
				fieldKey={fieldKey}
				items={Array.isArray(value) ? value : []}
				onChange={onChange}
				onEditingChange={onEditingChange}
			/>
		)
	}

	if (isArray) {
		return (
			<SelectPrimitiveTags
				fieldKey={fieldKey}
				list={value}
				onChange={onChange}
			/>
		)
	}

	if (typeof value === "boolean") {
		return (
			<div className="flex items-center justify-between">
				{fieldKey}
				<Switch checked={value} onChange={onChange} />
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-1.5">
			{fieldKey}
			{typeof value === "number" ? (
				<InputNumber value={value} onChange={(n) => onChange(n ?? 0)} />
			) : (
				<Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
			)}
		</div>
	)
}

/**
 * Inline Editable Table for Array of Objects (e.g. labels, resources, mappings).
 * Uses row-level edit mode. Cells become editable on "Edit" action.
 */
function EditableTable({
	items,
	onChange,
	onEditingChange,
}: {
	items: Record<string, any>[]
	onChange: (newItems: Record<string, any>[]) => void
	onEditingChange?: (isEditing: boolean) => void
	fieldKey?: string
}) {
	const [form] = Form.useForm()
	const [editingKey, setEditingKey] = useState<number | null>(null)

	const isEditing = useCallback(
		(rowIndex: number) => rowIndex === editingKey,
		[editingKey],
	)

	const edit = useCallback(
		(rowIndex: number, record: Record<string, any>) => {
			const { _isNew, ...cleanRecord } = record
			form.setFieldsValue(cleanRecord)
			setEditingKey(rowIndex)
		},
		[form],
	)

	const cancel = useCallback(() => {
		form.resetFields()
		if (editingKey === 0 && items[0]?._isNew) {
			onChange(items.slice(1))
		}
		setEditingKey(null)
	}, [editingKey, form, items, onChange])

	const saveRow = useCallback(
		async (rowIndex: number) => {
			try {
				const values = await form.validateFields()
				const updated = [...items]
				const { _isNew, ...existing } = items[rowIndex]
				updated[rowIndex] = { ...existing, ...values }
				onChange(updated)
				setEditingKey(null)
			} catch {
				// Form validation automatically highlights missing fields
			}
		},
		[form, items, onChange],
	)

	const handleDeleteRow = useCallback(
		(rowIndex: number) => {
			if (items.length <= 1) {
				return
			}
			onChange(items.filter((_, i) => i !== rowIndex))
			if (editingKey === rowIndex) {
				cancel()
			}
		},
		[items, onChange, editingKey, cancel],
	)

	const columns = useMemo(() => {
		const keySet = new Set<string>()
		for (const item of items) {
			if (typeof item === "object" && item !== null) {
				Object.keys(item).forEach((k) => {
					if (k !== "_isNew") {
						keySet.add(k)
					}
				})
			}
		}
		return Array.from(keySet)
	}, [items])

	const tableColumns = useMemo(
		() => [
			{
				title: "Actions",
				key: "operation",
				width: 140,
				render: (_: any, record: any, rowIndex: number) => {
					const editable = isEditing(rowIndex)
					return editable ? (
						<>
							<Tooltip.Dark title="Save">
								<Button
									type="text"
									size="large"
									aria-label="Save"
									icon={<SaveOutlined aria-hidden />}
									onClick={() => saveRow(rowIndex)}
								/>
							</Tooltip.Dark>
							<Tooltip.Dark title="Cancel">
								<Button
									type="text"
									size="large"
									aria-label="Cancel"
									icon={<CloseOutlined aria-hidden />}
									onClick={cancel}
								/>
							</Tooltip.Dark>
						</>
					) : (
						<>
							<Tooltip.Dark title="Edit">
								<Button
									type="text"
									size="large"
									aria-label="Edit"
									disabled={editingKey !== null}
									icon={<EditOutlined aria-hidden />}
									onClick={() => edit(rowIndex, record)}
								/>
							</Tooltip.Dark>
							<Tooltip.Dark
								title={
									items.length <= 1
										? "At least one entry is required"
										: "Delete"
								}
							>
								<Button
									danger
									type="text"
									size="large"
									aria-label="Delete"
									disabled={editingKey !== null || items.length <= 1}
									icon={<DeleteOutlined aria-hidden />}
									onClick={() => handleDeleteRow(rowIndex)}
								/>
							</Tooltip.Dark>
						</>
					)
				},
			},
			...columns.map((colKey) => {
				const isColArray = items.some((item) => Array.isArray(item[colKey]))
				const isColBoolean = items.some(
					(item) => typeof item[colKey] === "boolean",
				)

				return {
					title: colKey.charAt(0).toUpperCase() + colKey.slice(1),
					dataIndex: colKey,
					key: colKey,
					render: (value: any, _: any, rowIndex: number) => {
						const editable = isEditing(rowIndex)

						if (!editable) {
							if (Array.isArray(value)) {
								return value.join(", ")
							}
							if (typeof value === "boolean") {
								return value ? "Yes" : "No"
							}
							return String(value ?? "")
						}

						if (isColArray || Array.isArray(value)) {
							return (
								<Form.Item
									name={colKey}
									rules={[{ required: true, message: "Field is required" }]}
								>
									<SelectPrimitiveTags fieldKey={colKey} />
								</Form.Item>
							)
						}
						if (isColBoolean || typeof value === "boolean") {
							return (
								<Form.Item name={colKey} valuePropName="checked">
									<Switch />
								</Form.Item>
							)
						}
						return (
							<Form.Item
								name={colKey}
								rules={[{ required: true, message: "Field is required" }]}
							>
								<Input />
							</Form.Item>
						)
					},
				}
			}),
		],
		[
			cancel,
			columns,
			edit,
			editingKey,
			handleDeleteRow,
			isEditing,
			items,
			saveRow,
		],
	)

	useEffect(() => {
		onEditingChange?.(editingKey !== null)
	}, [editingKey, onEditingChange])

	// Auto-edit newly added row at index 0
	useEffect(() => {
		if (items.length > 0 && items[0]?._isNew && editingKey !== 0) {
			edit(0, items[0])
		}
	}, [items, editingKey, edit])

	return (
		<Form form={form} component={false}>
			<Table
				rowKey={(record) => JSON.stringify(record)}
				pagination={false}
				tableLayout="fixed"
				columns={tableColumns}
				dataSource={items}
			/>
		</Form>
	)
}

/**
 * Select Primitive Tags using Ant Design Select mode="tags" for primitive lists.
 * Allows adding custom tags/entries directly by typing and pressing enter or comma.
 */
function SelectPrimitiveTags({
	fieldKey,
	list,
	value,
	onChange,
}: {
	fieldKey: string
	list?: (string | number)[]
	value?: (string | number)[]
	onChange?: (newList: (string | number)[]) => void
}) {
	const rawList = useMemo(() => value ?? list ?? [], [list, value])
	const options = useMemo(
		() => rawList.map((item) => ({ label: item, value: item })),
		[rawList],
	)

	return (
		<Select
			mode="tags"
			virtual={false}
			value={rawList}
			className="min-w-2xs"
			notFoundContent={null}
			tokenSeparators={[","]}
			onChange={(newValues) => onChange?.(newValues)}
			placeholder={`Select or enter custom ${fieldKey}...`}
			options={options}
		/>
	)
}
